<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AppButton, AppLoading, useDialog, useToast } from 'duanaga-ui'
import FetchError from '@/FetchError'
import BatchApi from '@/api/Batch'
import CodeApi from '@/api/Code'
import BatchEditModal from '@/components/BatchEditModal.vue'
import CodeGenerateModal from '@/components/CodeGenerateModal.vue'
import { useDate } from '@/composables/date'
import useTitle from '@/composables/title'
import useBatchStore from '@/stores/batch'
import useCodeStore from '@/stores/code'

const route = useRoute()
const router = useRouter()
const { toastError } = useToast()
const { showDialog } = useDialog()
const batchStore = useBatchStore()
const codeStore = useCodeStore()

const batch = ref<Batch | null>(null)
// Removed refetch from storeToRefs since we use batchStore.refetch directly
const codes = ref<Code[]>([])
const loading = ref(false)
const loadingCodes = ref(false)
const error = ref<string | null>(null)
const codesError = ref<string | null>(null)

const batchId = computed(() => Number(route.params.id))
const title = useTitle('Batch Details')
const { formatHumanDateTime, formatHumanDate } = useDate()

// Computed property to determine if Send to Printer button should show
const shouldShowPrinterButton = computed(() => {
  return batch.value && batch.value.code_count > 0
})

onMounted(() => {
  void fetchBatch()
})

// Watch for batch changes and load codes when batch is available
watch(
  batch,
  (currentBatch) => {
    if (currentBatch) {
      void fetchCodes()
    }
  },
  { immediate: true }
)

// Watch for refetch signals from code store
watch(
  () => codeStore.refetch,
  (shouldRefetch) => {
    if (shouldRefetch && batch.value) {
      void fetchCodes()
      void fetchBatch() // Also refresh batch data to get updated code_count
      codeStore.setRefetch(false)
    }
  },
  { flush: 'post' } // Ensure this runs after DOM updates
)

// Watch for refetch signals from batch store
watch(
  () => batchStore.refetch,
  (shouldRefetch) => {
    if (shouldRefetch && batch.value) {
      void fetchBatch()
      batchStore.setRefetch(false)
    }
  },
  { flush: 'post' } // Ensure this runs after DOM updates
)

// Watch for direct batch store refetch signals
watch(
  () => batchStore.refetch,
  (shouldRefetch) => {
    if (shouldRefetch && batch.value) {
      void fetchBatch()
      batchStore.setRefetch(false)
    }
  }
)

const fetchBatch = async (): Promise<void> => {
  if (!batchId.value) {
    router.push({ name: 'batches' })
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await BatchApi.show<{ data: Batch }>(batchId.value)
    batch.value = response.data
    title.value = `Batch ${batch.value.number}`
  } catch (err) {
    const fetchError = err as FetchError
    error.value = fetchError.message
    toastError(fetchError.message)
  } finally {
    loading.value = false
  }
}

const fetchCodes = async (): Promise<void> => {
  if (!batch.value) return

  loadingCodes.value = true
  codesError.value = null

  try {
    const response = await CodeApi.get<Code[]>({ batch: batch.value.id })
    codes.value = response
  } catch (err) {
    const fetchError = err as FetchError
    codesError.value = fetchError.message
    toastError(fetchError.message)
  } finally {
    loadingCodes.value = false
  }
}

const showGenerateModal = (): void => {
  if (!batch.value) return

  // Set the selected batch in the store for the modal to use
  batchStore.selected = batch.value
  showDialog(CodeGenerateModal)
}

const showEditModal = (): void => {
  if (!batch.value) return

  // Set the selected batch in the store for the modal to use
  batchStore.selected = batch.value
  showDialog(BatchEditModal)
}

const showSendToPrinterModal = (): void => {
  if (!batch.value) return

  // Set the selected batch in the store for the modal to use
  batchStore.selected = batch.value
  showDialog(defineAsyncComponent(() => import('@/components/BatchSendToPrinterModal.vue')))
}

const goBack = (): void => {
  router.push({ name: 'batches' })
}
</script>

<template>
  <section class="flex flex-col gap-16 rounded-16 border border-grey-30 bg-white p-16 shadow-sm">
    <header class="flex items-center justify-between gap-16">
      <div class="flex items-center gap-16">
        <AppButton
          icon="keyboard_arrow_left"
          text="Back"
          variant="text"
          @click="goBack"
        />
        <div>
          <h1 class="text-20 font-600 text-indigo-110">
            {{ loading ? 'Loading...' : title }}
          </h1>
        </div>
      </div>

      <div
        v-if="batch && !loading"
        class="flex gap-12"
      >
        <AppButton
          text="Edit Batch"
          icon="edit"
          variant="outline"
          :disabled="loadingCodes"
          @click="showEditModal"
        />
        <AppButton
          v-if="shouldShowPrinterButton"
          text="Send to Printer"
          icon="print"
          color="green"
          :disabled="loadingCodes"
          @click="showSendToPrinterModal"
        />
        <AppButton
          color="black"
          text="Generate Codes"
          icon="add"
          :disabled="loadingCodes"
          @click="showGenerateModal"
        />
      </div>
    </header>

    <div v-if="loading">
      <AppLoading />
    </div>

    <div
      v-else-if="error"
      class="text-red-500"
    >
      {{ error }}
    </div>

    <div
      v-else-if="batch"
      class="flex flex-col gap-24"
    >
      <!-- Batch Information -->
      <section class="bg-grey-5 rounded-8 border border-grey-30 p-16">
        <h2 class="mb-16 text-16 font-600 text-indigo-110">Batch Information</h2>

        <div class="grid grid-cols-1 gap-16 md:grid-cols-2">
          <div>
            <label class="block text-12 font-500 uppercase tracking-[0.1em] text-grey-70">
              Batch Number
            </label>
            <p class="text-16 font-500 text-indigo-110">{{ batch.number }}</p>
          </div>

          <div>
            <label class="block text-12 font-500 uppercase tracking-[0.1em] text-grey-70">
              Brand
            </label>
            <p class="text-16 font-500 text-indigo-110">{{ batch.brand }}</p>
          </div>

          <div>
            <label class="block text-12 font-500 uppercase tracking-[0.1em] text-grey-70">
              Product
            </label>
            <p class="text-16 font-500 text-indigo-110">{{ batch.product }}</p>
          </div>

          <div>
            <label class="block text-12 font-500 uppercase tracking-[0.1em] text-grey-70">
              Variant
            </label>
            <p class="text-16 font-500 text-indigo-110">{{ batch.variant }}</p>
          </div>

          <div>
            <label class="block text-12 font-500 uppercase tracking-[0.1em] text-grey-70">
              Production Date
            </label>
            <p class="text-16 font-500 text-indigo-110">
              {{ formatHumanDate(batch.production_date) }}
            </p>
          </div>

          <div>
            <label class="block text-12 font-500 uppercase tracking-[0.1em] text-grey-70">
              Expiry Date
            </label>
            <p class="text-16 font-500 text-indigo-110">{{ formatHumanDate(batch.expiry_date) }}</p>
          </div>

          <div>
            <label class="block text-12 font-500 uppercase tracking-[0.1em] text-grey-70">
              Created At
            </label>
            <p class="text-16 font-500 text-indigo-110">
              {{ formatHumanDateTime(batch.created_at) }}
            </p>
          </div>
        </div>
      </section>

      <!-- Generated Codes -->
      <section class="rounded-8 border border-grey-30 bg-white">
        <div class="border-b border-grey-30 p-16">
          <h2 class="text-16 font-600 text-indigo-110">
            Generated Codes
            <span v-if="loadingCodes">(Loading...)</span>
            <span v-else>({{ codes?.length || 0 }})</span>
          </h2>
        </div>

        <div
          v-if="loadingCodes"
          class="p-16"
        >
          <AppLoading />
        </div>

        <div
          v-else-if="codesError"
          class="text-red-500 p-16"
        >
          {{ codesError }}
        </div>

        <div
          v-else-if="codes && codes.length > 0"
          class="p-16"
        >
          <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            <div
              v-for="code in codes"
              :key="code.id"
              class="rounded-6 bg-grey-5 font-mono border border-grey-30 p-12 text-14"
            >
              {{ code.code }}
            </div>
          </div>
        </div>

        <div
          v-else
          class="p-16 text-center"
        >
          <div class="py-24">
            <p class="mb-8 text-16 text-grey-70">No codes generated yet</p>
            <p class="text-14 text-grey-60">
              Click "Generate Codes" to create codes for this batch
            </p>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>
